import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Swords, Scroll, Dice6, Sparkles, Shield, Heart, Zap,
  Plus, Users, Map as MapIcon, LogOut, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Send, Bot, Skull, Crown, Wand2, BookOpen, Flame, Grid, Minus, RotateCcw, Play, SkipForward,
  Edit, Trash2, Upload, ImagePlus, X, Save, Settings as SettingsIcon, Mic, Video, PenTool,
  Info, Music, ZoomIn, ZoomOut, Locate, MessageSquare, Star, Pin, PinOff, Eye, EyeOff,
  Book, User as UserIcon, Backpack, ScrollText, History, Sparkle, Layers, Square, Circle,
  ArrowUpRight, Ruler, MousePointer2, Hand, PaintBucket, ExternalLink, Pause, StopCircle,
  Volume2, VolumeX
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api/mytharion-vtt`;

async function api(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const res = await fetch(`${API}${path}`, { ...options, headers, credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.detail?.message || data?.detail || data?.error || 'Request failed';
    const error = new Error(typeof message === 'string' ? message : JSON.stringify(message));
    error.status = res.status;
    error.detail = data?.detail;
    throw error;
  }
  return data;
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const RACES = ['Umano', 'Elfo', 'Mezzelfo', 'Nano delle Colline', 'Nano delle Montagne', 'Halfling', 'Dragonide', 'Gnomo', 'Mezzorco', 'Tiefling', 'Aasimar', 'Genasi', 'Goliath', 'Orco', 'Non-morto', 'Mostro', 'Altro'];
const CLASSES = ['Barbaro', 'Bardo', 'Chierico', 'Druido', 'Guerriero', 'Monaco', 'Paladino', 'Ranger', 'Ladro', 'Stregone', 'Warlock', 'Mago', 'Artefice', 'Mostro'];
const SKILLS_LIST = ['Acrobazia', 'Addestrare Animali', 'Arcano', 'Atletica', 'Furtività', 'Indagare', 'Inganno', 'Intimidire', 'Intrattenere', 'Intuizione', 'Medicina', 'Natura', 'Percezione', 'Persuasione', 'Rapidità di Mano', 'Religione', 'Sopravvivenza', 'Storia'];

const YT_CONTAINER_ID = 'mytharion-yt-player';

function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  // If it's already just an ID (11 chars, alphanumeric+_-)
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0];
    if (u.hostname.includes('youtube.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
      const parts = u.pathname.split('/').filter(Boolean);
      const embedIdx = parts.indexOf('embed');
      if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
      const shortsIdx = parts.indexOf('shorts');
      if (shortsIdx >= 0 && parts[shortsIdx + 1]) return parts[shortsIdx + 1];
    }
  } catch {}
  return null;
}

// ============ LANDING ============
function Landing({ onEnter }) {
  return (
    <div className="min-h-screen">
      <div className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1644051833659-25a1f5f241c4?w=1920&q=80)`,
          backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(3px)'
        }} />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />
      <div className="relative z-10">
        <nav className="container flex items-center justify-between py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full rune-border gold-glow flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-gold" />
            </div>
            <span className="font-display text-2xl text-gold">Mytharion</span>
          </div>
          <Button onClick={onEnter} className="bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-black font-heading gold-glow">
            Entra nella Cripta
          </Button>
        </nav>
        <section className="container py-20 md:py-32 text-center">
          <div className="animate-float inline-block mb-6">
            <div className="w-24 h-24 mx-auto rounded-full rune-border arcane-glow flex items-center justify-center">
              <Scroll className="w-12 h-12 text-gold" />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black text-gold mb-6 leading-tight">
            Forgia la tua<br /><span className="text-parchment">Leggenda</span>
          </h1>
          <p className="font-serif-fantasy text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10 italic">
            Un archivio vivente di miti e leggende. Il Virtual Tabletop dove Dungeon Master e giocatori creano, vivono e tramandano campagne fantasy indimenticabili.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={onEnter} size="lg" className="bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-black font-heading text-lg gold-glow px-8">
              <Swords className="mr-2 h-5 w-5" /> Inizia l'Avventura
            </Button>
            <Button onClick={onEnter} size="lg" variant="outline" className="border-purple-700 text-purple-200 hover:bg-purple-950/50 font-heading text-lg px-8">
              <BookOpen className="mr-2 h-5 w-5" /> Esplora il Demo
            </Button>
          </div>
          <div className="divider-fantasy w-64 mx-auto mt-16" />
        </section>
        <section className="container py-16">
          <h2 className="font-display text-3xl md:text-4xl text-center text-gold mb-16">Gli Strumenti dei Custodi</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MapIcon, title: 'Tavolo Virtuale', desc: 'Mappe interattive con griglia, token drag & drop e misurazione delle distanze.' },
              { icon: Dice6, title: 'Sistema Dadi Completo', desc: 'd4 fino a d100, con vantaggio, svantaggio, modificatori e cronologia.' },
              { icon: Shield, title: 'Schede D&D 5e / 2024', desc: 'Personaggi completi con statistiche, incantesimi, equipaggiamento e talenti.' },
              { icon: Bot, title: 'Loremaster Arcano', desc: 'IA privata al DM per creare NPC, mostri, quest, dungeon e lore in tempo reale.' }
            ].map((f, i) => (
              <Card key={i} className="rune-border bg-card/60 backdrop-blur">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-900/60 to-purple-950/60 flex items-center justify-center mb-3 gold-glow">
                    <f.icon className="w-6 h-6 text-gold" />
                  </div>
                  <CardTitle className="font-heading text-gold text-xl">{f.title}</CardTitle>
                  <CardDescription className="font-serif-fantasy text-base">{f.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>
        <footer className="container py-12 text-center text-muted-foreground border-t border-yellow-900/20 mt-16">
          <p className="font-serif-fantasy italic">« Ogni tiro di dado è una pagina della storia. Ogni campagna, una leggenda. »</p>
          <p className="text-sm mt-4">Mytharion © 2025 — Il Custode delle Leggende</p>
        </footer>
      </div>
    </div>
  );
}

// ============ AUTH ============
function AuthView({ onLogin, onBack }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api(`/auth/${mode === 'login' ? 'login' : 'register'}`, {
        method: 'POST', body: JSON.stringify({ email, password, displayName })
      });
      localStorage.setItem('mytharion_token', data.token);
      toast.success(mode === 'login' ? 'Bentornato, viaggiatore.' : 'La tua leggenda inizia ora.');
      onLogin(data.user);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1658702983847-e974f4ed2d55?w=1920&q=80)`, backgroundSize: 'cover' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-background/90 to-background" />
      <Card className="w-full max-w-md rune-border bg-card/95 backdrop-blur relative z-10 arcane-glow">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full rune-border gold-glow flex items-center justify-center mb-3">
            <Sparkles className="w-8 h-8 text-gold" />
          </div>
          <CardTitle className="font-display text-3xl text-gold">{mode === 'login' ? 'Torna al Tavolo' : 'Forgia il tuo Nome'}</CardTitle>
          <CardDescription className="font-serif-fantasy italic">{mode === 'login' ? 'I miti attendono il tuo ritorno' : 'Un nuovo capitolo delle leggende'}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <Label className="font-heading text-parchment">Nome Visualizzato</Label>
                <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Es. Grimwald il Saggio" className="bg-input font-serif-fantasy" />
              </div>
            )}
            <div><Label className="font-heading text-parchment">Email</Label><Input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="bg-input" /></div>
            <div><Label className="font-heading text-parchment">Password</Label><Input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="bg-input" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow">
              {loading ? 'Un momento...' : (mode === 'login' ? 'Entra' : 'Crea Account')}
            </Button>
          </form>
          <div className="text-center mt-6">
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-sm text-arcane hover:text-gold font-heading underline">
              {mode === 'login' ? 'Non hai un account? Forgia il tuo nome' : 'Hai già un account? Entra'}
            </button>
          </div>
          <button onClick={onBack} className="text-xs text-muted-foreground mt-4 mx-auto block hover:text-gold">← Torna all'ingresso</button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ DASHBOARD ============
function Dashboard({ user, onLogout, onOpenTable }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    try { const data = await api('/tables'); setTables(data.tables); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }

  async function createTable() {
    if (!newName.trim()) return toast.error('Dai un nome alla campagna');
    try {
      const data = await api('/tables', { method: 'POST', body: JSON.stringify({ name: newName, description: newDesc }) });
      setTables([data.table, ...tables]);
      setNewName(''); setNewDesc(''); setCreating(false);
      toast.success('Nuovo tavolo forgiato');
    } catch (e) { toast.error(e.message); }
  }

  async function joinTable() {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoinBusy(true);
    try {
      const data = await api('/tables/join', { method: 'POST', body: JSON.stringify({ inviteCode: code }) });
      toast.success(data.already ? 'Sei già in questo tavolo — apertura…' : 'Entrato al tavolo!');
      setJoinCode(''); setJoining(false);
      await load();
      onOpenTable(data.table_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setJoinBusy(false);
    }
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-yellow-900/30 bg-card/50 backdrop-blur">
        <div className="container flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full rune-border gold-glow flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-xl text-gold leading-none">Mytharion</div>
              <div className="text-xs text-muted-foreground">Custode: {user.displayName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="inline-flex items-center gap-2 h-9 px-3 rounded rune-border text-xs font-heading hover:bg-yellow-900/20 text-gold"
              title="Torna al sito Mytharion Store"
              data-testid="vtt-back-to-site"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> TORNA AL SITO
            </a>
            <Button variant="ghost" onClick={onLogout} className="text-muted-foreground hover:text-ruby">
              <LogOut className="h-4 w-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>
      <main className="container py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl text-gold mb-2">Le tue Campagne</h1>
            <p className="font-serif-fantasy italic text-muted-foreground">Scegli una leggenda da vivere o forgiane una nuova.</p>
          </div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow" data-testid="vtt-dashboard-new-campaign">
                <Plus className="mr-2 h-4 w-4" /> Nuova Campagna
              </Button>
            </DialogTrigger>
            <DialogContent className="rune-border bg-card">
              <DialogHeader><DialogTitle className="font-display text-2xl text-gold">Forgia una nuova Campagna</DialogTitle><DialogDescription className="font-serif-fantasy">Il primo passo verso una nuova leggenda.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label className="font-heading">Nome</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="La Cripta dei Sussurri" /></div>
                <div><Label className="font-heading">Descrizione</Label><Textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Un'antica cripta risvegliata..." rows={3} /></div>
              </div>
              <DialogFooter><Button onClick={createTable} className="bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading">Crea</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={joining} onOpenChange={setJoining}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="rune-border font-heading" data-testid="vtt-dashboard-join">
                <Users className="mr-2 h-4 w-4" /> Unisciti (codice)
              </Button>
            </DialogTrigger>
            <DialogContent className="rune-border bg-card">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-gold">Unisciti a un tavolo</DialogTitle>
                <DialogDescription className="font-serif-fantasy">
                  Inserisci il codice invito ricevuto dal tuo DM (formato MYTH-XXXXX).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="MYTH-ABCDE"
                  className="font-mono"
                  data-testid="vtt-dashboard-join-input"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={joinTable}
                  disabled={joinBusy || !joinCode.trim()}
                  data-testid="vtt-dashboard-join-submit"
                  className="bg-gradient-to-b from-arcane to-arcane/70 text-white font-heading"
                >
                  {joinBusy ? 'Ingresso…' : 'Entra al Tavolo'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {loading ? (
          <div className="text-center py-20 text-muted-foreground font-serif-fantasy italic">Consultando gli archivi antichi...</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tables.map(t => (
              <Card key={t.id} className="rune-border bg-card/80 backdrop-blur overflow-hidden group cursor-pointer hover:arcane-glow transition-all" onClick={() => onOpenTable(t.id)}>
                <div className="h-40 relative overflow-hidden">
                  <img src={t.imageUrl} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                  {t.isDemo && (<Badge className="absolute top-3 right-3 bg-purple-900/80 border-purple-500 text-purple-100 font-heading"><Crown className="w-3 h-3 mr-1" /> DEMO</Badge>)}
                  {t.myRole === 'member' && !t.isDemo && (<Badge className="absolute top-3 right-3 bg-arcane/80 border-arcane text-white font-heading" data-testid={`vtt-card-member-${t.id}`}><Users className="w-3 h-3 mr-1" /> GIOCATORE</Badge>)}
                  {t.myRole === 'owner' && !t.isDemo && (<Badge className="absolute top-3 right-3 bg-yellow-900/80 border-yellow-500 text-yellow-100 font-heading"><Crown className="w-3 h-3 mr-1" /> DM</Badge>)}
                </div>
                <CardHeader>
                  <CardTitle className="font-display text-xl text-gold group-hover:text-yellow-300">{t.name}</CardTitle>
                  <CardDescription className="font-serif-fantasy line-clamp-2">{t.description || 'Una nuova avventura ti attende.'}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Max {t.maxPlayers}</span>
                  <span className="font-mono text-xs text-arcane">{t.inviteCode}</span>
                  <ChevronRight className="w-5 h-5 text-gold group-hover:translate-x-1 transition" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ============ CHARACTER BUILDER DIALOG ============
function CharacterDialog({ open, onOpenChange, character, tableId, onSaved, onDeleted }) {
  const isEdit = !!character;
  const [form, setForm] = useState({
    isNpc: false, name: '', race: 'Umano', class: 'Guerriero', subclass: '', background: '', level: 1,
    hp: 10, maxHp: 10, ac: 10, initiative: 0, speed: 30,
    stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
    skills: [], inventory: [], spellSlots: {},
    tokenColor: '#d4af37', tokenImage: null, notes: ''
  });
  const [invText, setInvText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (character) {
      setForm({ ...character });
      setInvText((character.inventory || []).join('\n'));
    } else {
      setForm({
        isNpc: false, name: '', race: 'Umano', class: 'Guerriero', subclass: '', background: '', level: 1,
        hp: 10, maxHp: 10, ac: 10, initiative: 0, speed: 30,
        stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
        skills: [], inventory: [], spellSlots: {},
        tokenColor: '#d4af37', tokenImage: null, notes: ''
      });
      setInvText('');
    }
  }, [open, character]);

  const updStat = (s, v) => setForm(f => ({ ...f, stats: { ...f.stats, [s]: parseInt(v) || 0 } }));
  const toggleSkill = s => setForm(f => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s] }));
  const updSlot = (lvl, key, v) => setForm(f => ({ ...f, spellSlots: { ...f.spellSlots, [lvl]: { ...(f.spellSlots[lvl] || { max: 0, used: 0 }), [key]: parseInt(v) || 0 } } }));
  const removeSlot = lvl => setForm(f => { const s = { ...f.spellSlots }; delete s[lvl]; return { ...f, spellSlots: s }; });

  async function pickTokenImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Immagine troppo grande (max 500KB)'); return; }
    const dataUrl = await readFileAsDataURL(file);
    setForm(f => ({ ...f, tokenImage: dataUrl }));
  }

  async function save() {
    if (!form.name.trim()) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      const inventory = invText.split('\n').map(s => s.trim()).filter(Boolean);
      const payload = { ...form, inventory, tableId };
      const r = isEdit
        ? await api(`/characters/${character.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await api('/characters', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(isEdit ? 'Personaggio aggiornato' : 'Personaggio forgiato');
      onSaved(r.character, isEdit);
      onOpenChange(false);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function del() {
    if (!confirm('Eliminare definitivamente ' + character.name + '?')) return;
    try {
      await api(`/characters/${character.id}`, { method: 'DELETE' });
      toast.success('Personaggio eliminato');
      onDeleted(character.id);
      onOpenChange(false);
    } catch (e) { toast.error(e.message); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rune-border bg-card max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-gold">{isEdit ? 'Modifica Personaggio' : 'Forgia un Nuovo Personaggio'}</DialogTitle>
          <DialogDescription className="font-serif-fantasy">Scheda D&D 5e / 2024 · {form.isNpc ? 'PNG/Mostro' : 'Personaggio Giocante'}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="base" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-4 shrink-0">
            <TabsTrigger value="base">Base</TabsTrigger>
            <TabsTrigger value="stats">Stats & Combattimento</TabsTrigger>
            <TabsTrigger value="skills">Competenze & Slot</TabsTrigger>
            <TabsTrigger value="equipment">Equipaggiamento</TabsTrigger>
          </TabsList>
          <ScrollArea className="flex-1 pr-3 scrollbar-thin">
            <TabsContent value="base" className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-heading">Nome *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Kaelen Ombravento" /></div>
                <div><Label className="font-heading">Livello</Label><Input type="number" min="1" max="20" value={form.level} onChange={e => setForm(f => ({ ...f, level: parseInt(e.target.value) || 1 }))} /></div>
                <div><Label className="font-heading">Razza</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-input px-3 text-sm" value={form.race} onChange={e => setForm(f => ({ ...f, race: e.target.value }))}>
                    {RACES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div><Label className="font-heading">Classe</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-input px-3 text-sm" value={form.class} onChange={e => setForm(f => ({ ...f, class: e.target.value }))}>
                    {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label className="font-heading">Sottoclasse</Label><Input value={form.subclass} onChange={e => setForm(f => ({ ...f, subclass: e.target.value }))} placeholder="Cacciatore" /></div>
                <div><Label className="font-heading">Background</Label><Input value={form.background} onChange={e => setForm(f => ({ ...f, background: e.target.value }))} placeholder="Sopravvissuto" /></div>
              </div>
              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={form.isNpc} onCheckedChange={v => setForm(f => ({ ...f, isNpc: v }))} />
                  <span className="text-sm">{form.isNpc ? 'PNG / Mostro' : 'Personaggio Giocante'}</span>
                </label>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-heading">Colore Token</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.tokenColor} onChange={e => setForm(f => ({ ...f, tokenColor: e.target.value }))} className="w-16 h-10 rounded cursor-pointer" />
                    <Input value={form.tokenColor} onChange={e => setForm(f => ({ ...f, tokenColor: e.target.value }))} className="flex-1 font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="font-heading">Ritratto / Token (opz.)</Label>
                  <div className="flex items-center gap-2">
                    {form.tokenImage && <img src={form.tokenImage} alt="token" className="w-10 h-10 rounded-full object-cover border-2 border-yellow-700" />}
                    <label className="flex-1">
                      <input type="file" accept="image/*" onChange={pickTokenImage} className="hidden" />
                      <div className="h-10 flex items-center justify-center gap-2 border border-input rounded-md cursor-pointer hover:bg-muted/50 text-xs">
                        <ImagePlus className="w-4 h-4" /> {form.tokenImage ? 'Cambia' : 'Carica'} (max 500KB)
                      </div>
                    </label>
                    {form.tokenImage && <button type="button" onClick={() => setForm(f => ({ ...f, tokenImage: null }))} className="text-ruby hover:opacity-80"><X className="w-4 h-4" /></button>}
                  </div>
                </div>
              </div>
              <div><Label className="font-heading">Note / Sfondo</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Cerca il fratello scomparso..." /></div>
            </TabsContent>
            <TabsContent value="stats" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="font-heading text-ruby">HP Attuali</Label><Input type="number" value={form.hp} onChange={e => setForm(f => ({ ...f, hp: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label className="font-heading text-ruby">HP Max</Label><Input type="number" value={form.maxHp} onChange={e => setForm(f => ({ ...f, maxHp: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label className="font-heading text-arcane">Classe Armatura</Label><Input type="number" value={form.ac} onChange={e => setForm(f => ({ ...f, ac: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label className="font-heading text-gold">Iniziativa</Label><Input type="number" value={form.initiative} onChange={e => setForm(f => ({ ...f, initiative: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label className="font-heading">Velocità (ft)</Label><Input type="number" value={form.speed} onChange={e => setForm(f => ({ ...f, speed: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <Separator />
              <div>
                <Label className="font-heading text-gold mb-2 block">Caratteristiche</Label>
                <div className="grid grid-cols-6 gap-2">
                  {[['str', 'Forza'], ['dex', 'Destrezza'], ['con', 'Costituzione'], ['int', 'Intelligenza'], ['wis', 'Saggezza'], ['cha', 'Carisma']].map(([s, label]) => {
                    const mod = Math.floor((form.stats[s] - 10) / 2);
                    return (
                      <div key={s} className="rune-border rounded p-2 text-center">
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">{label}</div>
                        <Input type="number" min="1" max="30" value={form.stats[s]} onChange={e => updStat(s, e.target.value)} className="text-center h-8" />
                        <div className="text-xs text-gold mt-1">{mod >= 0 ? '+' : ''}{mod}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="skills" className="space-y-4 mt-4">
              <div>
                <Label className="font-heading text-gold mb-2 block">Competenze</Label>
                <div className="grid grid-cols-3 gap-1">
                  {SKILLS_LIST.map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted/40 rounded text-xs">
                      <input type="checkbox" checked={form.skills.includes(s)} onChange={() => toggleSkill(s)} />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-heading text-gold">Slot Incantesimi</Label>
                  <button onClick={() => setForm(f => {
                    const usedLvls = Object.keys(f.spellSlots).map(Number);
                    let next = 1;
                    while (usedLvls.includes(next)) next++;
                    if (next > 9) return f;
                    return { ...f, spellSlots: { ...f.spellSlots, [next]: { max: 1, used: 0 } } };
                  })} className="text-xs text-arcane hover:text-gold flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Aggiungi Livello
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(form.spellSlots).sort(([a], [b]) => Number(a) - Number(b)).map(([lvl, s]) => (
                    <div key={lvl} className="rune-border rounded p-2 flex items-center gap-2">
                      <span className="text-xs font-heading text-gold w-12">Liv {lvl}</span>
                      <Input type="number" min="0" value={s.used} onChange={e => updSlot(lvl, 'used', e.target.value)} className="h-7 text-center" />
                      <span className="text-xs">/</span>
                      <Input type="number" min="0" value={s.max} onChange={e => updSlot(lvl, 'max', e.target.value)} className="h-7 text-center" />
                      <button onClick={() => removeSlot(lvl)} className="text-ruby hover:opacity-80"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                {Object.keys(form.spellSlots).length === 0 && <p className="text-xs text-muted-foreground italic">Nessuno slot. Aggiungi se sei un incantatore.</p>}
              </div>
            </TabsContent>
            <TabsContent value="equipment" className="space-y-4 mt-4">
              <div>
                <Label className="font-heading text-gold mb-1 block">Inventario (uno per riga)</Label>
                <Textarea value={invText} onChange={e => setInvText(e.target.value)} rows={10} placeholder="Arco lungo +1&#10;Faretra (24 frecce)" className="font-serif-fantasy" />
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
        <DialogFooter className="flex-row justify-between items-center gap-2">
          {isEdit && (<Button variant="outline" onClick={del} className="text-ruby border-ruby/50 hover:bg-ruby/10"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>)}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Salvo...' : (isEdit ? 'Aggiorna' : 'Forgia')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ GAME VIEW - Redesigned HUD ============
function GameView({ tableId, user, onExit }) {
  const [data, setData] = useState(null);
  const [selectedChar, setSelectedChar] = useState(null);
  const [pinnedChar, setPinnedChar] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [draggingToken, setDraggingToken] = useState(null);
  const [lastRoll, setLastRoll] = useState(null);
  const [charDialogOpen, setCharDialogOpen] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  const [uploadingMap, setUploadingMap] = useState(false);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const [drawTool, setDrawTool] = useState('select');
  const [webcamOn, setWebcamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [rightPage, setRightPage] = useState('spells');
  const [favorites, setFavorites] = useState({});
  // AI
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiScrollRef = useRef(null);
  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  // Dice popover state
  const [diceModifier, setDiceModifier] = useState(0);
  const [diceLabel, setDiceLabel] = useState('');
  const [diceAdvantage, setDiceAdvantage] = useState(false);
  const [diceDisadvantage, setDiceDisadvantage] = useState(false);
  const [diceHidden, setDiceHidden] = useState(false);
  // Music
  const [musicUrlInput, setMusicUrlInput] = useState('');
  const [localVolume, setLocalVolume] = useState(50);
  const [muted, setMuted] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const ytPlayerRef = useRef(null);
  const isOwner = true; // MVP: only DM opens tables; will be dynamic in multi-user Phase 2
  // Settings
  const [fontPx, setFontPx] = useState(16);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  // Cartomancer license visibility (topbar shortcut)
  const [hasCartomancer, setHasCartomancer] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND}/api/users/me/licenses`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { licenses: [] }))
      .then((d) => {
        const owns = (d.licenses || []).some(
          (l) => l.status === 'active' && (l.product_id === 'cartomancer_basic' || l.product_id === 'cartomancer_premium'),
        );
        setHasCartomancer(owns);
      })
      .catch(() => {});
  }, []);

  // Cursor-centered wheel zoom + middle-mouse pan on the map viewport
  const isMiddlePanning = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const middlePanStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  // Zoom/pan refs so the non-passive wheel handler always reads the latest values
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);
  // Attach a non-passive wheel listener so we can call preventDefault without React warning
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = -e.deltaY > 0 ? 1.1 : 1 / 1.1;
      const currentZoom = zoomRef.current;
      const currentPan = panRef.current;
      const newZoom = Math.min(3, Math.max(0.2, currentZoom * factor));
      if (newZoom === currentZoom) return;
      const worldX = (cx - currentPan.x) / currentZoom;
      const worldY = (cy - currentPan.y) / currentZoom;
      setPan({ x: cx - worldX * newZoom, y: cy - worldY * newZoom });
      setZoom(newZoom);
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);
  const handleMapPointerDown = (e) => {
    // Middle mouse OR left-click on empty map (not on a token) pans the map
    const clickedToken = e.target?.closest?.('[data-vtt-token]');
    if (e.button === 1 || (e.button === 0 && !clickedToken)) {
      e.preventDefault();
      isMiddlePanning.current = true;
      setIsPanning(true);
      middlePanStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    }
  };
  const handleMapPointerMoveViewport = (e) => {
    if (isMiddlePanning.current) {
      setPan({
        x: middlePanStart.current.panX + (e.clientX - middlePanStart.current.x),
        y: middlePanStart.current.panY + (e.clientY - middlePanStart.current.y),
      });
    }
  };
  const handleMapPointerUpViewport = () => {
    if (isMiddlePanning.current) {
      isMiddlePanning.current = false;
      setIsPanning(false);
    }
  };

  // ─── WebSocket real-time sync ────────────────────────────────────────────
  const wsRef = useRef(null);
  const [wsPeers, setWsPeers] = useState(1);
  useEffect(() => {
    if (!tableId) return;
    const wsBase = BACKEND.replace(/^http/, 'ws');
    const url = `${wsBase}/api/mytharion-vtt/ws/${tableId}`;
    let closed = false;
    let reconnectTimer = null;
    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { /* connected */ };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'hello') { setWsPeers(msg.peers || 1); return; }
          if (msg.type === 'presence') {
            setWsPeers((p) => Math.max(1, p + (msg.action === 'join' ? 1 : -1)));
            return;
          }
          if (msg.type === 'dice.roll' && msg.roll) {
            setData((d) => d ? { ...d, rolls: [msg.roll, ...(d.rolls || [])].slice(0, 100) } : d);
            return;
          }
          if (msg.type === 'ai.message') {
            setData((d) => d ? { ...d, aiMessages: [...(d.aiMessages || []), msg.userMessage, msg.assistantMessage] } : d);
            return;
          }
          if (msg.type === 'table.patch' && msg.table) {
            setData((d) => d ? { ...d, table: { ...d.table, ...msg.table } } : d);
            return;
          }
          if (msg.type === 'chat.message') {
            setData((d) => d ? { ...d, aiMessages: [...(d.aiMessages || []), { id: msg.id, role: 'player', content: `${msg.userName}: ${msg.content}`, createdAt: msg.createdAt }] } : d);
            return;
          }
        } catch {}
      };
      ws.onclose = () => {
        if (closed) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch {}
    };
  }, [tableId]);

  // Load settings from LS
  useEffect(() => {
    const saved = parseInt(localStorage.getItem('mytharion_fontpx') || '16');
    setFontPx(saved);
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty('--font-scale', fontPx + 'px');
    localStorage.setItem('mytharion_fontpx', String(fontPx));
  }, [fontPx]);

  // Load LS music preferences
  useEffect(() => {
    const v = parseInt(localStorage.getItem('mytharion_vol') || '50');
    const m = localStorage.getItem('mytharion_muted') === 'true';
    setLocalVolume(v);
    setMuted(m);
  }, []);
  useEffect(() => {
    localStorage.setItem('mytharion_vol', String(localVolume));
    localStorage.setItem('mytharion_muted', String(muted));
  }, [localVolume, muted]);

  // Load YouTube IFrame API once (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.YT && window.YT.Player) { setYtReady(true); return; }
    if (!document.getElementById('yt-iframe-api-script')) {
      const tag = document.createElement('script');
      tag.id = 'yt-iframe-api-script';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }
    const prevCb = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      setYtReady(true);
      if (typeof prevCb === 'function') prevCb();
    };
    // Poll fallback in case callback already fired
    const iv = setInterval(() => {
      if (window.YT && window.YT.Player) { setYtReady(true); clearInterval(iv); }
    }, 300);
    return () => clearInterval(iv);
  }, []);

  // Poll music state every 3s to broadcast to all clients
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const d = await api(`/tables/${tableId}`);
        setData(prev => {
          if (!prev) return prev;
          const remoteMusic = d.table.music || null;
          const localMusic = prev.table.music || null;
          // Prefer newer state via updatedAt to avoid clobbering fresh local changes
          const localTs = localMusic?.updatedAt || '';
          const remoteTs = remoteMusic?.updatedAt || '';
          if (localTs && remoteTs && remoteTs < localTs) return prev;
          if (JSON.stringify(remoteMusic) === JSON.stringify(localMusic)) return prev;
          return { ...prev, table: { ...prev.table, music: remoteMusic } };
        });
      } catch {}
    }, 3000);
    return () => clearInterval(iv);
  }, [tableId]);

  useEffect(() => { load(); }, [tableId]);

  useEffect(() => {
    if (aiScrollRef.current) aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
  }, [data?.aiMessages, aiLoading]);

  async function load() {
    try {
      const d = await api(`/tables/${tableId}`);
      setData(d);
      if (d.characters?.length && !selectedChar) setSelectedChar(d.characters[0]);
    } catch (e) { toast.error(e.message); }
  }

  const displayedChar = pinnedChar || selectedChar;

  async function saveTokens(tokens) {
    try { await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ tokens }) }); }
    catch (e) { toast.error('Errore salvataggio: ' + e.message); }
  }

  const startDrag = (e, token) => {
    if (drawTool !== 'select' && drawTool !== 'hand') return;
    e.stopPropagation();
    const rect = mapRef.current.getBoundingClientRect();
    setDraggingToken({
      id: token.id,
      offsetX: (e.clientX - rect.left) / zoom - token.x,
      offsetY: (e.clientY - rect.top) / zoom - token.y
    });
  };
  const onMapMove = (e) => {
    if (!draggingToken) return;
    const rect = mapRef.current.getBoundingClientRect();
    const grid = data.table.map.gridSize;
    let x = (e.clientX - rect.left) / zoom - draggingToken.offsetX;
    let y = (e.clientY - rect.top) / zoom - draggingToken.offsetY;
    if (data.table.map.snapToGrid) {
      x = Math.round(x / grid) * grid;
      y = Math.round(y / grid) * grid;
    }
    setData(prev => ({ ...prev, table: { ...prev.table, tokens: prev.table.tokens.map(t => t.id === draggingToken.id ? { ...t, x, y } : t) } }));
  };
  const endDrag = () => {
    if (draggingToken && data) { saveTokens(data.table.tokens); setDraggingToken(null); }
  };

  async function rollDice(sides, count = 1) {
    try {
      const d = await api('/dice/roll', {
        method: 'POST',
        body: JSON.stringify({
          tableId, sides, count, modifier: diceModifier,
          advantage: diceAdvantage && sides === 20,
          disadvantage: diceDisadvantage && sides === 20,
          label: diceLabel, hidden: diceHidden
        })
      });
      setLastRoll(d.roll);
      setData(prev => ({ ...prev, rolls: [d.roll, ...(prev.rolls || [])].slice(0, 50) }));
      toast.success(`${d.roll.formula} → ${d.roll.total}`, { description: d.roll.label });
    } catch (e) { toast.error(e.message); }
  }

  async function sendAi() {
    if (!aiInput.trim()) return;
    const msg = aiInput;
    setAiInput(''); setAiLoading(true);
    const optimistic = { id: 'temp-' + Date.now(), tableId, role: 'user', content: msg, createdAt: new Date().toISOString() };
    setData(prev => ({ ...prev, aiMessages: [...(prev.aiMessages || []), optimistic] }));
    try {
      const d = await api('/ai/chat', { method: 'POST', body: JSON.stringify({ tableId, message: msg }) });
      setData(prev => ({ ...prev, aiMessages: [...(prev.aiMessages || []).filter(m => m.id !== optimistic.id), d.userMessage, d.assistantMessage] }));
    } catch (e) {
      toast.error(e.message);
      setData(prev => ({ ...prev, aiMessages: (prev.aiMessages || []).filter(m => m.id !== optimistic.id) }));
    } finally { setAiLoading(false); }
  }

  async function updateHp(char, delta) {
    const newHp = Math.max(0, Math.min(char.maxHp, char.hp + delta));
    const newTokens = data.table.tokens.map(t => t.characterId === char.id ? { ...t, hp: newHp } : t);
    setData(prev => ({
      ...prev,
      characters: prev.characters.map(c => c.id === char.id ? { ...c, hp: newHp } : c),
      table: { ...prev.table, tokens: newTokens }
    }));
    if (selectedChar?.id === char.id) setSelectedChar(prev => ({ ...prev, hp: newHp }));
    if (pinnedChar?.id === char.id) setPinnedChar(prev => ({ ...prev, hp: newHp }));
    try {
      await api(`/characters/${char.id}`, { method: 'PATCH', body: JSON.stringify({ hp: newHp }) });
      await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ tokens: newTokens }) });
    } catch {}
  }

  async function rollInitiative() {
    const order = data.characters.map(c => ({
      characterId: c.id, name: c.name,
      roll: Math.floor(Math.random() * 20) + 1 + (c.initiative || 0),
      isNpc: c.isNpc, hp: c.hp, maxHp: c.maxHp
    })).sort((a, b) => b.roll - a.roll);
    const initiative = { active: true, order, currentIdx: 0, round: 1 };
    setData(prev => ({ ...prev, table: { ...prev.table, initiative } }));
    await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ initiative }) });
    toast.success('Iniziativa: ' + order[0].name + ' apre le danze.');
  }
  async function nextTurn() {
    if (!data.table.initiative.active) return;
    const init = { ...data.table.initiative };
    init.currentIdx = (init.currentIdx + 1) % init.order.length;
    if (init.currentIdx === 0) init.round += 1;
    setData(prev => ({ ...prev, table: { ...prev.table, initiative: init } }));
    await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ initiative: init }) });
  }
  async function endInitiative() {
    const initiative = { active: false, order: [], currentIdx: 0, round: 1 };
    setData(prev => ({ ...prev, table: { ...prev.table, initiative } }));
    await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ initiative }) });
  }
  async function uploadMap(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Immagine troppo grande (max 3MB)'); return; }
    setUploadingMap(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const newMap = { ...data.table.map, imageUrl: dataUrl };
      setData(prev => ({ ...prev, table: { ...prev.table, map: newMap } }));
      await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ map: newMap }) });
      toast.success('Mappa caricata');
    } catch (err) { toast.error(err.message); }
    finally { setUploadingMap(false); e.target.value = ''; }
  }
  async function toggleGrid() {
    const newMap = { ...data.table.map, showGrid: !data.table.map.showGrid };
    setData(prev => ({ ...prev, table: { ...prev.table, map: newMap } }));
    await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ map: newMap }) });
  }

  function centerOnToken() {
    if (!displayedChar) return;
    const tok = data.table.tokens.find((t) => t.characterId === displayedChar.id);
    if (!tok) return;
    const container = mapContainerRef.current;
    if (!container) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } else {
      const rect = container.getBoundingClientRect();
      // Center token in the visible viewport at current zoom
      const targetZoom = zoom;
      setPan({
        x: rect.width / 2 - tok.x * targetZoom,
        y: rect.height / 2 - tok.y * targetZoom,
      });
    }
    toast.info('Centrato su ' + displayedChar.name);
  }

  function onCharSaved(saved, isEdit) {
    if (isEdit) {
      setData(prev => ({
        ...prev,
        characters: prev.characters.map(c => c.id === saved.id ? saved : c),
        table: { ...prev.table, tokens: prev.table.tokens.map(t => t.characterId === saved.id ? { ...t, name: saved.name, color: saved.tokenColor, hp: saved.hp, maxHp: saved.maxHp, isNpc: saved.isNpc } : t) }
      }));
      setSelectedChar(saved);
    } else {
      const gs = data.table.map.gridSize;
      const newToken = {
        id: (crypto?.randomUUID?.() || 'tok-' + Date.now()),
        characterId: saved.id, name: saved.name, color: saved.tokenColor,
        x: Math.round((data.table.map.width / 2) / gs) * gs,
        y: Math.round((data.table.map.height / 2) / gs) * gs,
        size: 1, hp: saved.hp, maxHp: saved.maxHp, isNpc: saved.isNpc, conditions: []
      };
      const newTokens = [...data.table.tokens, newToken];
      setData(prev => ({ ...prev, characters: [...prev.characters, saved], table: { ...prev.table, tokens: newTokens } }));
      api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ tokens: newTokens }) });
      setSelectedChar(saved);
    }
  }
  function onCharDeleted(charId) {
    setData(prev => ({
      ...prev,
      characters: prev.characters.filter(c => c.id !== charId),
      table: { ...prev.table, tokens: prev.table.tokens.filter(t => t.characterId !== charId) }
    }));
    if (selectedChar?.id === charId) setSelectedChar(null);
    if (pinnedChar?.id === charId) setPinnedChar(null);
  }

  function toggleFav(charId, key) {
    setFavorites(prev => {
      const cur = new Set(prev[charId] || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...prev, [charId]: Array.from(cur) };
    });
  }
  const isFav = (charId, key) => (favorites[charId] || []).includes(key);

  // ---- MUSIC: sync input with music state, control player ----
  const musicState = data?.table?.music || null;
  const musicVideoId = musicState?.videoId || null;
  const musicIsPlaying = !!musicState?.isPlaying;

  useEffect(() => { if (musicState?.url && !musicUrlInput) setMusicUrlInput(musicState.url); }, [musicState?.url]);

  // Create / update / destroy YT player
  useEffect(() => {
    if (!ytReady || typeof window === 'undefined') return;
    const container = document.getElementById(YT_CONTAINER_ID);
    if (!container) return;

    if (!musicVideoId) {
      // destroy player if exists
      if (ytPlayerRef.current && typeof ytPlayerRef.current.destroy === 'function') {
        try { ytPlayerRef.current.destroy(); } catch {}
        ytPlayerRef.current = null;
        // Re-add empty container div because destroy() removes it
        const parent = document.getElementById(YT_CONTAINER_ID + '-wrap');
        if (parent && !document.getElementById(YT_CONTAINER_ID)) {
          const d = document.createElement('div');
          d.id = YT_CONTAINER_ID;
          parent.appendChild(d);
        }
      }
      return;
    }

    if (ytPlayerRef.current && typeof ytPlayerRef.current.loadVideoById === 'function') {
      try {
        if (musicIsPlaying) ytPlayerRef.current.loadVideoById({ videoId: musicVideoId, suggestedQuality: 'small' });
        else ytPlayerRef.current.cueVideoById({ videoId: musicVideoId, suggestedQuality: 'small' });
      } catch {}
      return;
    }

    if (window.YT && window.YT.Player) {
      ytPlayerRef.current = new window.YT.Player(YT_CONTAINER_ID, {
        videoId: musicVideoId,
        playerVars: {
          autoplay: musicIsPlaying ? 1 : 0,
          controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3,
          modestbranding: 1, playsinline: 1, rel: 0
        },
        events: {
          onReady: (e) => {
            try {
              e.target.setVolume(muted ? 0 : localVolume);
              if (musicIsPlaying) e.target.playVideo();
            } catch {}
          }
        }
      });
    }
  }, [ytReady, musicVideoId]);

  // React to play/pause changes
  useEffect(() => {
    const p = ytPlayerRef.current;
    if (!p || !musicVideoId) return;
    try {
      if (musicIsPlaying && typeof p.playVideo === 'function') p.playVideo();
      else if (!musicIsPlaying && typeof p.pauseVideo === 'function') p.pauseVideo();
    } catch {}
  }, [musicIsPlaying, musicVideoId]);

  // React to volume/mute changes (local only)
  useEffect(() => {
    const p = ytPlayerRef.current;
    if (!p || typeof p.setVolume !== 'function') return;
    try { p.setVolume(muted ? 0 : localVolume); } catch {}
  }, [localVolume, muted]);

  async function updateMusicState(patch) {
    const cur = data?.table?.music || {};
    const newMusic = { ...cur, ...patch, updatedAt: new Date().toISOString() };
    setData(prev => ({ ...prev, table: { ...prev.table, music: newMusic } }));
    try { await api(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify({ music: newMusic }) }); }
    catch (e) { toast.error(e.message); }
  }
  async function musicPlay() {
    const vid = extractYouTubeId(musicUrlInput);
    if (!vid) { toast.error('URL YouTube non valido'); return; }
    await updateMusicState({ url: musicUrlInput, videoId: vid, isPlaying: true });
    toast.success('Musica avviata per tutti');
  }
  async function musicPause() {
    if (!musicVideoId) return;
    await updateMusicState({ isPlaying: false });
  }
  async function musicResume() {
    if (!musicVideoId) return;
    await updateMusicState({ isPlaying: true });
  }
  async function musicStop() {
    await updateMusicState({ url: '', videoId: null, isPlaying: false });
    setMusicUrlInput('');
    toast.info('Musica fermata');
  }

  function ytEmbed(url) {
    const id = extractYouTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center font-serif-fantasy italic text-muted-foreground">Aprendo il portale...</div>;

  const { table, characters, rolls, aiMessages } = data;
  const gridSize = table.map.gridSize;
  const activeInitChar = table.initiative.active ? table.initiative.order[table.initiative.currentIdx] : null;

  // ---- TOP TOOLBAR ----
  const TopToolbar = () => (
    <header className="h-14 shrink-0 border-b border-yellow-900/40 bg-gradient-to-b from-[#1a1520] to-[#0f0d15] flex items-center px-2 gap-1">
      <button onClick={onExit} className="text-muted-foreground hover:text-gold px-2 h-9 flex items-center gap-1 rounded hover:bg-muted/30 text-sm" data-testid="vtt-back-to-dashboard">
        <ChevronLeft className="w-4 h-4" /> Archivio
      </button>
      <a href="/" className="text-muted-foreground hover:text-gold px-2 h-9 flex items-center gap-1 rounded hover:bg-muted/30 text-xs" data-testid="vtt-game-back-to-site" title="Torna al sito Mytharion Store">
        <ExternalLink className="w-3.5 h-3.5" /> Sito
      </a>
      {hasCartomancer && (
        <a
          href="/app"
          className="text-gold hover:text-gold/80 px-2 h-9 flex items-center gap-1 rounded hover:bg-yellow-900/20 text-xs font-heading"
          data-testid="vtt-topbar-cartomancer"
          title="Apri Cartomancer (pagina separata)"
        >
          <BookOpen className="w-3.5 h-3.5" /> Cartomancer
        </a>
      )}
      <Separator orientation="vertical" className="h-6 mx-1" />

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><SettingsIcon className="w-4 h-4 text-gold" /> Impostazioni</button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rune-border bg-card">
          <div className="space-y-4">
            <div>
              <div className="font-display text-gold text-sm mb-2">Impostazioni Interfaccia</div>
              <Label className="text-xs">Dimensione Font: <span className="font-mono text-gold">{fontPx}px</span></Label>
              <Slider min={12} max={20} step={1} value={[fontPx]} onValueChange={v => setFontPx(v[0])} className="mt-2" />
              <div className="text-[10px] text-muted-foreground mt-1">Da 12 a 20 pixel (scala rem globale)</div>
            </div>
            <Separator />
            <div className="text-xs space-y-2">
              <div className="font-heading text-gold">Pannelli</div>
              <label className="flex items-center justify-between cursor-pointer"><span>Colonna Sinistra</span><Switch checked={!leftCollapsed} onCheckedChange={v => setLeftCollapsed(!v)} /></label>
              <label className="flex items-center justify-between cursor-pointer"><span>Colonna Destra</span><Switch checked={!rightCollapsed} onCheckedChange={v => setRightCollapsed(!v)} /></label>
              <label className="flex items-center justify-between cursor-pointer"><span>Barra Inferiore</span><Switch checked={!bottomCollapsed} onCheckedChange={v => setBottomCollapsed(!v)} /></label>
              <label className="flex items-center justify-between cursor-pointer"><span>Griglia mappa</span><Switch checked={table.map.showGrid} onCheckedChange={toggleGrid} /></label>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <button onClick={() => { setMicOn(!micOn); toast.info(micOn ? 'Microfono spento' : 'Microfono attivato'); }} className={`h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm ${micOn ? 'text-gold' : 'text-parchment'}`} data-testid="vtt-topbar-mic" title={micOn ? 'Spegni microfono' : 'Accendi microfono'}>
        <Mic className="w-4 h-4" /> Mic
      </button>
      <button onClick={() => { setWebcamOn(!webcamOn); toast.info(webcamOn ? 'Webcam spenta' : 'Webcam attivata'); }} className={`h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm ${webcamOn ? 'text-gold' : 'text-parchment'}`} data-testid="vtt-topbar-webcam" title={webcamOn ? 'Spegni webcam' : 'Accendi webcam'}>
        <Video className="w-4 h-4" /> Webcam
      </button>
      <div
        className="h-9 px-2 flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider text-arcane rune-border rounded"
        title={`${wsPeers} partecipant${wsPeers === 1 ? 'e' : 'i'} sul tavolo in tempo reale`}
        data-testid="vtt-topbar-live"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Live · {wsPeers}
      </div>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <button onClick={() => setDrawTool(drawTool === 'pen' ? 'select' : 'pen')} className={`h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm ${drawTool === 'pen' ? 'text-gold' : 'text-parchment'}`}>
        <PenTool className="w-4 h-4" /> Disegno
      </button>

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><Info className="w-4 h-4 text-gold" /> Informazioni</button>
        </PopoverTrigger>
        <PopoverContent className="w-96 rune-border bg-card">
          <div className="space-y-3">
            <div className="font-display text-gold">Diario del Master</div>
            <div className="text-xs font-serif-fantasy text-muted-foreground italic">Cartelle organizzabili: NPC, Luoghi, Missioni, Immagini, Tabelle, Note, Documenti.</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {['NPC', 'Luoghi', 'Missioni', 'Immagini', 'Tabelle', 'Note'].map(c => (
                <div key={c} className="rune-border rounded p-2 flex items-center gap-2 text-parchment"><Scroll className="w-3 h-3 text-gold" /> {c}</div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Pannello base — contenuti condivisibili nella prossima iterazione.</p>
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <button className={`h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm ${musicIsPlaying ? 'text-gold' : 'text-parchment'}`}>
            <Music className={`w-4 h-4 ${musicIsPlaying ? 'text-gold animate-pulse' : 'text-gold'}`} /> Musica
            {musicIsPlaying && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse ml-1" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 rune-border bg-card" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full rune-border gold-glow flex items-center justify-center"><Music className="w-4 h-4 text-gold" /></div>
              <div>
                <div className="font-display text-gold text-sm leading-none">Riproduttore Musicale</div>
                <div className="text-[10px] text-muted-foreground">Audio-only · Broadcast a tutto il tavolo</div>
              </div>
            </div>

            {isOwner ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs font-heading text-gold">URL YouTube</Label>
                  <Input
                    value={musicUrlInput}
                    onChange={e => setMusicUrlInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') musicPlay(); }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="text-sm h-8"
                  />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {!musicIsPlaying && (
                    <Button size="sm" onClick={musicVideoId ? musicResume : musicPlay} className="col-span-2 bg-gradient-to-b from-green-700 to-green-900 hover:from-green-600 text-white font-heading">
                      <Play className="w-3.5 h-3.5 mr-1" /> {musicVideoId ? 'Riprendi' : 'Play'}
                    </Button>
                  )}
                  {musicIsPlaying && (
                    <Button size="sm" onClick={musicPause} className="col-span-2 bg-gradient-to-b from-yellow-700 to-yellow-900 hover:from-yellow-600 text-black font-heading">
                      <Pause className="w-3.5 h-3.5 mr-1" /> Pausa
                    </Button>
                  )}
                  <Button size="sm" onClick={musicPlay} disabled={!musicUrlInput} variant="outline" className="col-span-1 border-yellow-800/50 font-heading" title="Carica nuovo URL">
                    <Upload className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" onClick={musicStop} disabled={!musicVideoId} variant="outline" className="col-span-1 border-ruby/50 text-ruby hover:bg-ruby/10">
                    <StopCircle className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-xs font-serif-fantasy italic text-muted-foreground">Solo il Dungeon Master può gestire la musica del tavolo.</div>
            )}

            {musicVideoId && (
              <div className="rune-border rounded p-2 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${musicIsPlaying ? 'bg-green-400 animate-pulse shadow-[0_0_6px_#4ade80]' : 'bg-yellow-500'}`} />
                  <span className="text-parchment font-heading">{musicIsPlaying ? 'In riproduzione' : 'In pausa'}</span>
                  <span className="text-muted-foreground text-[10px] font-mono ml-auto">{musicVideoId}</span>
                </div>
                <div className="text-[10px] text-muted-foreground font-serif-fantasy italic truncate">{musicState?.url}</div>
              </div>
            )}

            <Separator />

            {/* Local volume + mute (each user, personal) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-heading text-gold flex items-center gap-1.5">
                  <button onClick={() => setMuted(!muted)} className={muted ? 'text-ruby' : 'text-gold hover:opacity-80'} title={muted ? 'Riattiva audio' : 'Silenzia'}>
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  Volume locale
                </Label>
                <span className="font-mono text-xs text-gold">{muted ? 'MUTO' : localVolume + '%'}</span>
              </div>
              <Slider
                min={0} max={100} step={5} value={[muted ? 0 : localVolume]}
                onValueChange={v => { setLocalVolume(v[0]); if (v[0] > 0 && muted) setMuted(false); if (v[0] === 0) setMuted(true); }}
                className="mt-1"
              />
              <div className="text-[10px] text-muted-foreground italic mt-1">Il volume è personale per ogni giocatore.</div>
            </div>

            <div className="text-[10px] text-muted-foreground font-serif-fantasy italic border-t border-yellow-900/30 pt-2">
              🎵 Continua in background quando chiudi il popup. Video nascosto per alleggerire il carico.
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} className="h-9 px-2 rounded hover:bg-muted/40 text-parchment"><ZoomOut className="w-4 h-4" /></button>
      <span className="text-xs font-mono w-12 text-center">{Math.round(zoom * 100)}%</span>
      <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="h-9 px-2 rounded hover:bg-muted/40 text-parchment"><ZoomIn className="w-4 h-4" /></button>
      <button onClick={centerOnToken} className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><Locate className="w-4 h-4 text-gold" /> Centra</button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><Dice6 className="w-4 h-4 text-gold" /> Dadi</button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rune-border bg-card" align="end">
          <div className="space-y-3">
            <Input placeholder="Etichetta tiro (opz.)" value={diceLabel} onChange={e => setDiceLabel(e.target.value)} className="text-sm h-8" />
            <div>
              <Label className="text-xs">Modificatore: <span className="text-gold font-mono">{diceModifier >= 0 ? '+' : ''}{diceModifier}</span></Label>
              <Slider min={-10} max={20} step={1} value={[diceModifier]} onValueChange={v => setDiceModifier(v[0])} />
            </div>
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={diceAdvantage} onCheckedChange={v => { setDiceAdvantage(v); if (v) setDiceDisadvantage(false); }} /><span className="text-green-400">Vantaggio</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={diceDisadvantage} onCheckedChange={v => { setDiceDisadvantage(v); if (v) setDiceAdvantage(false); }} /><span className="text-ruby">Svantaggio</span></label>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={diceHidden} onCheckedChange={setDiceHidden} /><span>Solo DM (nascosto)</span></label>
            <div className="grid grid-cols-4 gap-2">
              {[4, 6, 8, 10, 12, 20, 100].map(s => (
                <button key={s} onClick={() => rollDice(s)} className="rune-border rounded-lg p-2 hover:gold-glow group aspect-square flex flex-col items-center justify-center">
                  <Dice6 className="w-4 h-4 text-gold mb-0.5" />
                  <span className="font-display text-xs">d{s}</span>
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><Wand2 className="w-4 h-4 text-arcane" /> Loremaster</button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] rune-border bg-card" align="end">
          <div className="flex flex-col h-[500px]">
            <div className="flex items-center gap-2 pb-2 border-b border-yellow-900/30">
              <div className="w-8 h-8 rounded-full rune-border arcane-glow flex items-center justify-center"><Wand2 className="w-4 h-4 text-arcane" /></div>
              <div>
                <div className="font-display text-sm text-gold leading-none">Loremaster Arcano</div>
                <div className="text-[10px] text-muted-foreground">Privato al DM · Gemini Flash</div>
              </div>
            </div>
            <div ref={aiScrollRef} className="flex-1 overflow-y-auto py-2 space-y-2 scrollbar-thin">
              {(!aiMessages || aiMessages.length === 0) && (
                <div className="text-center py-6 space-y-3">
                  <Wand2 className="w-8 h-8 text-arcane mx-auto opacity-50" />
                  <p className="font-serif-fantasy italic text-xs text-muted-foreground">Il Loremaster attende.</p>
                  <div className="space-y-1">
                    {['Genera un NPC misterioso', 'Crea un mostro CR 3', 'Suggerisci 3 quest hooks', 'Descrivi il primo ambiente'].map(s => (
                      <button key={s} onClick={() => setAiInput(s)} className="block w-full text-left text-xs px-2 py-1 rune-border rounded hover:bg-muted/40">✦ {s}</button>
                    ))}
                  </div>
                </div>
              )}
              {aiMessages?.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${m.role === 'user' ? 'bg-yellow-900/30 border border-yellow-700/40' : 'bg-purple-950/40 border border-purple-800/40'}`}>
                    <div className="text-[9px] font-heading mb-0.5 opacity-70">{m.role === 'user' ? user.displayName : 'Loremaster'}</div>
                    <div className="font-serif-fantasy whitespace-pre-wrap leading-relaxed">{m.content}</div>
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div className="flex gap-1 pl-2"><span className="w-2 h-2 rounded-full bg-arcane animate-bounce"></span><span className="w-2 h-2 rounded-full bg-arcane animate-bounce" style={{ animationDelay: '150ms' }}></span><span className="w-2 h-2 rounded-full bg-arcane animate-bounce" style={{ animationDelay: '300ms' }}></span></div>
              )}
            </div>
            <div className="pt-2 border-t border-yellow-900/30 flex gap-2">
              <Textarea value={aiInput} onChange={e => setAiInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAi(); } }}
                placeholder="Chiedi al Loremaster..." rows={2} className="text-sm resize-none" disabled={aiLoading} />
              <Button onClick={sendAi} disabled={aiLoading || !aiInput.trim()} size="icon" className="bg-gradient-to-b from-purple-700 to-purple-900 shrink-0"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2.5 flex items-center gap-1.5 rounded hover:bg-muted/40 text-sm text-parchment"><Book className="w-4 h-4 text-gold" /> Glossario</button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rune-border bg-card" align="end">
          <div className="space-y-2">
            <div className="font-display text-gold">Glossario</div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {['Regole', 'Razze', 'Classi', 'Talenti', 'Incantesimi', 'Oggetti', 'Condizioni', 'Mostri', 'NPC', 'Luoghi'].map(c => (
                <div key={c} className="rune-border rounded p-1.5 text-center text-parchment">{c}</div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Contenuti in arrivo.</p>
          </div>
        </PopoverContent>
      </Popover>

      <div className="flex-1" />

      <Popover>
        <PopoverTrigger asChild>
          <button className="h-9 px-2 flex items-center gap-2 rounded hover:bg-muted/40 text-sm text-parchment">
            <div className="w-8 h-8 rounded-full rune-border flex items-center justify-center text-xs font-heading text-gold">{user.displayName.slice(0, 2).toUpperCase()}</div>
            <span>DM</span>
            <ChevronDown className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 rune-border bg-card" align="end">
          <div className="space-y-1">
            <div className="text-sm font-heading text-gold">{user.displayName}</div>
            <div className="text-xs text-muted-foreground">{user.email}</div>
            <Separator className="my-2" />
            <label className="w-full">
              <input type="file" accept="image/*" onChange={uploadMap} className="hidden" />
              <div className="text-xs px-2 py-1.5 rounded hover:bg-muted/40 cursor-pointer flex items-center gap-2"><Upload className="w-3 h-3" /> Carica mappa</div>
            </label>
            <button onClick={onExit} className="w-full text-xs px-2 py-1.5 rounded hover:bg-muted/40 flex items-center gap-2 text-ruby"><LogOut className="w-3 h-3" /> Esci dal tavolo</button>
          </div>
        </PopoverContent>
      </Popover>
    </header>
  );

  // ---- LEFT COLUMN ----
  const LeftPanel = () => (
    <aside className={`shrink-0 border-r border-yellow-900/40 bg-[#0d0b12]/95 backdrop-blur transition-all overflow-hidden ${leftCollapsed ? 'w-0' : 'w-72'}`}>
      {!leftCollapsed && (
        <div className="w-72 h-full flex flex-col">
          {/* Webcam header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-yellow-900/30">
            <span className="text-[10px] tracking-widest font-heading text-gold">◈ WEBCAM</span>
            <button onClick={() => setWebcamOn(!webcamOn)} className="text-muted-foreground hover:text-gold">
              {webcamOn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Webcam grid */}
          <div className="p-2 grid grid-cols-2 gap-1.5">
            {characters.filter(c => !c.isNpc).slice(0, 6).map(c => (
              <div key={c.id} className="aspect-video rounded relative overflow-hidden bg-[#111119] border border-yellow-900/40">
                {c.tokenImage ? (
                  <img src={c.tokenImage} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground font-serif-fantasy italic">No Webcam</div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 py-0.5">
                  <div className="text-[10px] font-heading text-parchment truncate">{c.name.split(' ')[0]}</div>
                </div>
                <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_4px_#4ade80]" />
              </div>
            ))}
            {/* DM slot */}
            <div className="aspect-video rounded relative overflow-hidden bg-[#111119] border border-purple-800/50">
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center font-heading text-gold text-xs">{user.displayName.slice(0, 2).toUpperCase()}</div>
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 py-0.5">
                <div className="text-[10px] font-heading text-gold">DM</div>
              </div>
            </div>
          </div>

          {/* Combat bar */}
          <div className="px-3 py-2 border-y border-yellow-900/30 mt-1">
            {table.initiative.active ? (
              <>
                <div className="flex items-center justify-between mb-1">
                  <Badge className="bg-ruby/20 border-ruby/60 text-ruby text-[10px] font-heading">◉ COMBATTIMENTO ATTIVO</Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-parchment">Round <span className="font-mono text-gold">{table.initiative.round}</span></span>
                  <button onClick={endInitiative} className="text-[10px] px-2 py-0.5 border border-ruby/60 text-ruby rounded hover:bg-ruby/10 font-heading">Fine Combattimento</button>
                </div>
                <div className="text-[11px] mt-1 text-parchment">Turno: <span className="text-gold font-heading">{activeInitChar?.name}</span></div>
              </>
            ) : (
              <button onClick={rollInitiative} className="w-full h-9 rounded bg-gradient-to-b from-red-900/60 to-red-950/80 border border-ruby/50 text-yellow-100 font-heading text-sm flex items-center justify-center gap-2 hover:from-red-800/60">
                <Swords className="w-4 h-4" /> Avvia Combattimento
              </button>
            )}
          </div>

          {/* Initiative tracker - only when active */}
          {table.initiative.active && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="text-center text-[10px] tracking-widest font-heading text-gold py-1 border-b border-yellow-900/30">◈ INIZIATIVA</div>
              <ScrollArea className="flex-1 scrollbar-thin">
                <div className="p-2 space-y-1">
                  {table.initiative.order.map((it, idx) => {
                    const c = characters.find(ch => ch.id === it.characterId);
                    const active = idx === table.initiative.currentIdx;
                    return (
                      <div key={it.characterId} className={`flex items-center gap-2 p-1.5 rounded ${active ? 'bg-ruby/20 border border-ruby/60' : 'hover:bg-muted/30'}`}>
                        <div className={`w-6 text-center font-mono text-xs ${active ? 'text-ruby' : 'text-muted-foreground'}`}>{idx + 1}</div>
                        {c?.tokenImage ? (
                          <img src={c.tokenImage} alt="" className="w-7 h-7 rounded-full object-cover border" style={{ borderColor: it.isNpc ? '#dc2626' : '#d4af37' }} />
                        ) : (
                          <div className="w-7 h-7 rounded-full border" style={{ backgroundColor: c?.tokenColor, borderColor: it.isNpc ? '#dc2626' : '#d4af37' }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-heading truncate">{it.name} {c && !it.isNpc && <span className="text-muted-foreground text-[10px]">({c.class})</span>}</div>
                          <div className="text-[10px] text-muted-foreground">HP {c?.hp}/{c?.maxHp}</div>
                        </div>
                        <div className="font-display text-lg text-gold">{it.roll}</div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Characters list (always visible, replaces initiative when combat off) */}
          {!table.initiative.active && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-yellow-900/30">
                <span className="text-[10px] tracking-widest font-heading text-gold">◈ PERSONAGGI</span>
                <button onClick={() => { setEditingChar(null); setCharDialogOpen(true); }} className="text-xs text-arcane hover:text-gold flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Aggiungi
                </button>
              </div>
              <ScrollArea className="flex-1 scrollbar-thin">
                <div className="p-2 space-y-1">
                  {characters.map(c => (
                    <div key={c.id} className={`group flex items-center gap-2 rounded p-1.5 cursor-pointer ${selectedChar?.id === c.id ? 'bg-yellow-900/30 border border-yellow-700/50' : 'hover:bg-muted/30'}`} onClick={() => setSelectedChar(c)}>
                      {c.tokenImage ? (
                        <img src={c.tokenImage} alt="" className="w-7 h-7 rounded-full object-cover border" style={{ borderColor: c.isNpc ? '#dc2626' : '#d4af37' }} />
                      ) : (
                        <div className="w-7 h-7 rounded-full border" style={{ backgroundColor: c.tokenColor, borderColor: c.isNpc ? '#dc2626' : '#d4af37' }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-heading truncate">{c.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{c.isNpc ? <><Skull className="w-2.5 h-2.5 inline" /> {c.race}</> : `${c.race} · ${c.class} L${c.level}`}</div>
                      </div>
                      <div className="text-[10px] font-mono text-ruby">{c.hp}/{c.maxHp}</div>
                      <button onClick={(e) => { e.stopPropagation(); setEditingChar(c); setCharDialogOpen(true); }} className="opacity-0 group-hover:opacity-100 text-arcane hover:text-gold"><Edit className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Footer buttons */}
          <div className="border-t border-yellow-900/30 p-2 flex items-center gap-1">
            <button onClick={() => table.initiative.active && nextTurn()} disabled={!table.initiative.active} className="h-7 px-2 rune-border rounded hover:bg-muted/30 disabled:opacity-40"><ChevronUp className="w-3.5 h-3.5 text-gold" /></button>
            <button onClick={() => table.initiative.active && nextTurn()} disabled={!table.initiative.active} className="h-7 px-2 rune-border rounded hover:bg-muted/30 disabled:opacity-40"><ChevronDown className="w-3.5 h-3.5 text-gold" /></button>
            <button onClick={() => { setEditingChar(null); setCharDialogOpen(true); }} className="flex-1 h-7 rune-border rounded hover:bg-muted/30 flex items-center justify-center gap-1 text-xs font-heading">
              <Plus className="w-3 h-3" /> Aggiungi
            </button>
            <button onClick={load} className="h-7 px-2 rune-border rounded hover:bg-muted/30"><RotateCcw className="w-3.5 h-3.5 text-gold" /></button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="h-7 px-2 rune-border rounded hover:bg-muted/30"><SettingsIcon className="w-3.5 h-3.5 text-gold" /></button>
              </PopoverTrigger>
              <PopoverContent className="w-64 rune-border bg-card">
                <div className="text-xs space-y-2">
                  <div className="font-heading text-gold">Impostazioni Combattimento</div>
                  <p className="text-muted-foreground text-[10px]">Opzioni avanzate arriveranno con i moduli.</p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}
    </aside>
  );

  // ---- RIGHT COLUMN (Character sheet contextual) ----
  const RightPanel = () => {
    if (!displayedChar) {
      return (
        <aside className={`shrink-0 border-l border-yellow-900/40 bg-[#0d0b12]/95 backdrop-blur transition-all ${rightCollapsed ? 'w-0' : 'w-96'}`}>
          {!rightCollapsed && (
            <div className="w-96 h-full flex items-center justify-center p-6 text-center">
              <div>
                <UserIcon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="font-serif-fantasy italic text-muted-foreground text-sm">Seleziona un token o un personaggio per vederne la scheda.</p>
              </div>
            </div>
          )}
        </aside>
      );
    }
    const c = displayedChar;
    const charSpells = ['Palla di Fuoco', 'Mano Magica', 'Scudo', 'Raggio di Gelo', 'Individuazione del Magico'];
    return (
      <aside className={`shrink-0 border-l border-yellow-900/40 bg-[#0d0b12]/95 backdrop-blur transition-all overflow-hidden ${rightCollapsed ? 'w-0' : 'w-96'}`}>
        {!rightCollapsed && (
          <div className="w-96 h-full flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b border-yellow-900/30">
              <div className="flex items-center gap-1 text-[10px] tracking-widest font-heading text-gold">
                <ChevronLeft className="w-3 h-3" /> SCHEDA PERSONAGGIO
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPinnedChar(pinnedChar?.id === c.id ? null : c)} className={pinnedChar?.id === c.id ? 'text-gold' : 'text-muted-foreground hover:text-gold'}>
                  {pinnedChar?.id === c.id ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => setRightCollapsed(true)} className="text-muted-foreground hover:text-gold"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            <ScrollArea className="flex-1 scrollbar-thin">
              <div className="p-3 space-y-3">
                {/* Portrait + info */}
                <div className="flex gap-3">
                  <div className="w-24 h-32 rounded overflow-hidden shrink-0 rune-border">
                    {c.tokenImage ? (
                      <img src={c.tokenImage} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: c.tokenColor }}>
                        <span className="font-display text-2xl text-white">{c.name.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl text-gold leading-tight">{c.name}</h2>
                    <p className="text-xs text-muted-foreground font-serif-fantasy italic">{c.isNpc ? <><Skull className="w-3 h-3 inline" /> {c.race}</> : `${c.class} ${c.race}${c.subclass ? ' (' + c.subclass + ')' : ''}`}</p>
                    <p className="text-xs text-parchment mt-0.5">Livello <span className="font-heading text-gold">{c.level}</span></p>
                    {!c.isNpc && (
                      <div className="mt-2">
                        <div className="text-[10px] text-muted-foreground mb-0.5">XP {c.level * 640} / {c.level * 1300}</div>
                        <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-yellow-600 to-yellow-400" style={{ width: '49%' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={() => { setEditingChar(c); setCharDialogOpen(true); }} className="w-full bg-transparent border border-yellow-800/50 hover:bg-yellow-900/20 text-parchment font-heading text-xs">
                  <ExternalLink className="w-3 h-3 mr-1" /> APRI SCHEDA COMPLETA
                </Button>

                {/* Tab icons */}
                <div className="flex items-center justify-between gap-1 py-2 border-y border-yellow-900/30">
                  {[
                    { k: 'overview', I: UserIcon }, { k: 'combat', I: Swords }, { k: 'stats', I: Shield },
                    { k: 'skills', I: Book }, { k: 'spells', I: Flame }, { k: 'favorites', I: Star },
                    { k: 'inventory', I: Backpack }, { k: 'bio', I: ScrollText }, { k: 'history', I: History },
                    { k: 'crown', I: Crown }
                  ].map(({ k, I }) => (
                    <button key={k} onClick={() => setRightPage(k)} title={k}
                      className={`w-8 h-8 rounded flex items-center justify-center transition ${rightPage === k ? 'bg-yellow-900/40 gold-glow text-gold' : 'text-muted-foreground hover:text-gold hover:bg-muted/30'}`}>
                      <I className="w-3.5 h-3.5" />
                    </button>
                  ))}
                </div>

                {/* Content per page */}
                {rightPage === 'spells' && (
                  <>
                    <h3 className="font-display text-lg text-gold">Incantesimi Rapidi</h3>
                    <div className="grid grid-cols-6 gap-2">
                      <div className="col-span-2 rune-border rounded p-2 text-center">
                        <Shield className="w-4 h-4 mx-auto text-arcane mb-1" />
                        <div className="text-[10px] text-muted-foreground">CD INCANTESIMI</div>
                        <div className="font-display text-2xl text-parchment">{8 + Math.floor((c.stats?.int - 10) / 2) + Math.ceil(c.level / 4)}</div>
                        <div className="text-[10px] text-muted-foreground">BONUS ATTACCO</div>
                        <div className="font-display text-lg text-gold">+{Math.floor((c.stats?.int - 10) / 2) + Math.ceil(c.level / 4) + 1}</div>
                      </div>
                      <div className="col-span-4">
                        <div className="text-[10px] text-muted-foreground mb-1 tracking-widest">SLOT INCANTESIMI</div>
                        <div className="grid grid-cols-5 gap-1">
                          {[1, 2, 3, 4, 5].map(lv => {
                            const s = c.spellSlots?.[lv];
                            return (
                              <div key={lv} className={`rune-border rounded p-1.5 text-center ${s ? '' : 'opacity-40'}`}>
                                <div className="text-[9px] text-muted-foreground">{lv}°</div>
                                <div className="font-mono text-sm text-parchment">{s ? `${s.max - s.used}/${s.max}` : '0/0'}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] text-muted-foreground mb-1 tracking-widest">INCANTESIMI PREPARATI</div>
                      <div className="space-y-1.5">
                        {charSpells.map((s, i) => (
                          <div key={s} className="rune-border rounded p-2 flex items-center gap-2 group">
                            <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-950 to-purple-900 flex items-center justify-center">
                              <Flame className="w-4 h-4 text-arcane" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-heading text-parchment truncate">{s}</div>
                              <div className="text-[10px] text-muted-foreground">Evocazione {i}° · 1 Azione</div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => rollDice(20, 1)} className="text-[10px] px-1.5 py-0.5 rune-border rounded text-gold font-mono">+7</button>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="text-muted-foreground hover:text-gold"><Info className="w-3.5 h-3.5" /></button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 rune-border bg-card">
                                  <div className="space-y-2 text-xs">
                                    <div className="font-display text-lg text-gold">{s}</div>
                                    <div className="grid grid-cols-2 gap-1 text-[10px]">
                                      <div><span className="text-muted-foreground">Tempo:</span> 1 Azione</div>
                                      <div><span className="text-muted-foreground">Portata:</span> 150 ft</div>
                                      <div><span className="text-muted-foreground">Componenti:</span> V, S, M</div>
                                      <div><span className="text-muted-foreground">Durata:</span> Istantanea</div>
                                    </div>
                                    <p className="font-serif-fantasy italic text-parchment">Una sfera brillante di fuoco esplode nel punto scelto.</p>
                                    <Button size="sm" onClick={() => rollDice(6, 8)} className="w-full bg-gradient-to-b from-yellow-600 to-yellow-800 text-black text-xs">Lancia (8d6)</Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                              <button onClick={() => toggleFav(c.id, 'spell-' + s)} className={isFav(c.id, 'spell-' + s) ? 'text-gold' : 'text-muted-foreground hover:text-gold'}>
                                <Star className="w-3.5 h-3.5" fill={isFav(c.id, 'spell-' + s) ? 'currentColor' : 'none'} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-2 text-xs font-heading">MOSTRA TUTTI GLI INCANTESIMI</Button>
                    </div>
                  </>
                )}

                {rightPage === 'overview' && (
                  <div className="space-y-3">
                    <h3 className="font-display text-lg text-gold">Panoramica</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rune-border p-2 rounded text-center">
                        <Heart className="w-4 h-4 mx-auto text-ruby" />
                        <div className="font-mono text-lg text-parchment">{c.hp}/{c.maxHp}</div>
                        <div className="text-[9px] text-muted-foreground">HP</div>
                        <div className="flex gap-1 mt-1 justify-center">
                          <button onClick={() => updateHp(c, -1)} className="text-[10px] px-1 bg-ruby/20 rounded">-1</button>
                          <button onClick={() => updateHp(c, -5)} className="text-[10px] px-1 bg-ruby/20 rounded">-5</button>
                          <button onClick={() => updateHp(c, +1)} className="text-[10px] px-1 bg-green-900/40 rounded">+1</button>
                        </div>
                      </div>
                      <div className="rune-border p-2 rounded text-center">
                        <Shield className="w-4 h-4 mx-auto text-arcane" />
                        <div className="font-mono text-lg text-parchment">{c.ac}</div>
                        <div className="text-[9px] text-muted-foreground">AC</div>
                      </div>
                      <div className="rune-border p-2 rounded text-center">
                        <Zap className="w-4 h-4 mx-auto text-gold" />
                        <div className="font-mono text-lg text-parchment">+{c.initiative}</div>
                        <div className="text-[9px] text-muted-foreground">Iniz</div>
                      </div>
                    </div>
                    {c.notes && <div className="text-xs font-serif-fantasy italic text-muted-foreground bg-muted/20 p-2 rounded border-l-2 border-yellow-700/50">« {c.notes} »</div>}
                  </div>
                )}

                {rightPage === 'stats' && (
                  <div className="space-y-3">
                    <h3 className="font-display text-lg text-gold">Caratteristiche</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(s => {
                        const val = c.stats[s]; const mod = Math.floor((val - 10) / 2);
                        return (
                          <div key={s} className="rune-border rounded p-2 text-center">
                            <div className="text-[10px] uppercase text-muted-foreground">{s}</div>
                            <div className="font-mono text-lg">{val}</div>
                            <div className="text-sm text-gold">{mod >= 0 ? '+' : ''}{mod}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {rightPage === 'skills' && (
                  <div className="space-y-2">
                    <h3 className="font-display text-lg text-gold">Competenze</h3>
                    <div className="flex flex-wrap gap-1">
                      {(c.skills || []).map(s => <Badge key={s} variant="outline" className="text-[10px] border-yellow-800/40">{s}</Badge>)}
                      {(!c.skills || c.skills.length === 0) && <p className="text-xs text-muted-foreground italic">Nessuna competenza registrata.</p>}
                    </div>
                  </div>
                )}

                {rightPage === 'inventory' && (
                  <div className="space-y-2">
                    <h3 className="font-display text-lg text-gold">Inventario</h3>
                    <ul className="text-xs font-serif-fantasy space-y-1 text-parchment">
                      {(c.inventory || []).map((it, i) => <li key={i} className="rune-border rounded px-2 py-1">◦ {it}</li>)}
                      {(!c.inventory || c.inventory.length === 0) && <p className="text-muted-foreground italic">Zaino vuoto.</p>}
                    </ul>
                  </div>
                )}

                {rightPage === 'favorites' && (
                  <div className="space-y-2">
                    <h3 className="font-display text-lg text-gold">Preferiti</h3>
                    {(favorites[c.id] || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">Nessun preferito. Clicca ⭐ su un incantesimo o oggetto per aggiungerlo qui.</p>
                    ) : (
                      <div className="space-y-1">
                        {(favorites[c.id] || []).map(k => (
                          <div key={k} className="rune-border rounded p-2 flex items-center gap-2">
                            <Star className="w-3 h-3 text-gold" fill="currentColor" />
                            <span className="text-xs font-serif-fantasy flex-1">{k.replace(/^spell-/, '')}</span>
                            <button onClick={() => toggleFav(c.id, k)} className="text-muted-foreground hover:text-ruby"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {['combat', 'bio', 'history', 'crown'].includes(rightPage) && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    <Sparkle className="w-8 h-8 mx-auto opacity-30 mb-2" />
                    <p className="font-serif-fantasy italic text-xs">Pagina «{rightPage}» in arrivo.</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </aside>
    );
  };

  // ---- DRAWING TOOLBAR ----
  const DrawingToolbar = () => (
    <div className="w-11 shrink-0 bg-[#0d0b12]/95 border-r border-yellow-900/40 py-2 flex flex-col items-center gap-1">
      {[
        { k: 'layers', I: Layers, label: 'Livelli' },
        { k: 'hand', I: Hand, label: 'Sposta' },
        { k: 'select', I: MousePointer2, label: 'Seleziona' },
        { k: 'pen', I: PenTool, label: 'Disegno' },
        { k: 'line', I: Minus, label: 'Linea' },
        { k: 'rect', I: Square, label: 'Rettangolo' },
        { k: 'circle', I: Circle, label: 'Cerchio' },
        { k: 'arrow', I: ArrowUpRight, label: 'Freccia' },
        { k: 'ping', I: Locate, label: 'Ping' },
        { k: 'ruler', I: Ruler, label: 'Righello' }
      ].map(({ k, I, label }) => (
        <button key={k} onClick={() => setDrawTool(k)} title={label}
          className={`w-8 h-8 rounded flex items-center justify-center ${drawTool === k ? 'bg-yellow-900/40 text-gold gold-glow' : 'text-muted-foreground hover:text-gold hover:bg-muted/30'}`}>
          <I className="w-4 h-4" />
        </button>
      ))}
      <div className="flex-1" />
      <div className="w-6 h-6 rounded rune-border" style={{ backgroundColor: '#c62828' }} title="Colore" />
      <div className="w-8 h-8 rounded flex items-center justify-center text-muted-foreground text-[10px] font-mono rune-border">3</div>
      <button onClick={() => { setDrawTool('select'); toast.info('Modalità selezione'); }} className="w-8 h-8 rounded flex items-center justify-center text-muted-foreground hover:text-ruby hover:bg-muted/30" data-testid="vtt-tool-erase" title="Torna in modalità selezione">
        <Trash2 className="w-4 h-4" />
      </button>
      <button onClick={() => document.documentElement.requestFullscreen?.()} className="w-8 h-8 rounded flex items-center justify-center text-muted-foreground hover:text-gold hover:bg-muted/30">
        <ExternalLink className="w-4 h-4" />
      </button>
    </div>
  );

  // ---- BOTTOM PANEL: rolls + chat ----
  const BottomPanel = () => (
    <div className={`shrink-0 border-t border-yellow-900/40 bg-[#0d0b12]/95 backdrop-blur transition-all overflow-hidden ${bottomCollapsed ? 'h-0' : 'h-52'}`}>
      {!bottomCollapsed && (
        <div className="h-52 grid grid-cols-2 gap-0">
          {/* Rolls */}
          <div className="border-r border-yellow-900/30 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-yellow-900/30">
              <span className="text-[10px] tracking-widest font-heading text-gold">◈ CRONOLOGIA TIRI</span>
              <History className="w-3 h-3 text-muted-foreground" />
            </div>
            <ScrollArea className="flex-1 scrollbar-thin">
              <div className="p-2 space-y-1">
                {(rolls || []).length === 0 && <p className="text-xs text-center text-muted-foreground italic py-6">Nessun tiro. Che la sorte decida.</p>}
                {rolls?.map(r => (
                  <div key={r.id} className="flex items-center gap-2 text-xs px-1 py-1 hover:bg-muted/20 rounded">
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{r.hidden ? 'DM' : r.userName?.slice(0, 4).toUpperCase()}</Badge>
                    <span className="font-heading text-parchment truncate flex-1">{r.label || `Tiro ${r.formula}`}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{r.formula}</span>
                    <span className="font-display text-base text-gold w-8 text-right">{r.total}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(r.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="px-2 py-1 border-t border-yellow-900/30 flex items-center gap-2 text-[10px]">
              <select className="bg-input rounded border border-input px-1 py-0.5"><option>Tutti i tiri</option><option>Solo miei</option><option>Solo DM</option></select>
              <div className="flex-1" />
              <SettingsIcon className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">DM · Privato · Pubblico</span>
              <button className="text-muted-foreground hover:text-ruby ml-2"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>

          {/* Chat */}
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-yellow-900/30">
              <span className="text-[10px] tracking-widest font-heading text-gold">◈ CHAT</span>
              <MessageSquare className="w-3 h-3 text-muted-foreground" />
            </div>
            <ScrollArea className="flex-1 scrollbar-thin">
              <div className="p-2 space-y-1.5">
                {chatMessages.length === 0 && (
                  <>
                    <div className="text-xs px-1">
                      <span className="font-heading text-gold mr-2">Loremaster</span>
                      <span className="text-muted-foreground text-[10px]">{new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                      <div className="font-serif-fantasy text-parchment">Il tavolo è pronto. La chat multi-utente arriverà con il modulo realtime.</div>
                    </div>
                  </>
                )}
                {chatMessages.map((m, i) => (
                  <div key={i} className="text-xs px-1">
                    <span className="font-heading text-gold mr-2">{m.author}</span>
                    <span className="text-muted-foreground text-[10px]">{m.time}</span>
                    <div className="font-serif-fantasy text-parchment">{m.text}</div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="px-2 py-1 border-t border-yellow-900/30 flex items-center gap-1">
              <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    setChatMessages([...chatMessages, { author: user.displayName, time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), text: chatInput }]);
                    setChatInput('');
                  }
                }}
                placeholder="Scrivi un messaggio..." className="h-7 text-xs" />
              <button className="text-muted-foreground hover:text-gold px-1">😊</button>
              <button onClick={() => {
                if (!chatInput.trim()) return;
                setChatMessages([...chatMessages, { author: user.displayName, time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), text: chatInput }]);
                setChatInput('');
              }} className="text-gold hover:opacity-80 px-1"><Send className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#0a0810]">
      {TopToolbar()}

      <div className="flex-1 flex overflow-hidden relative">
        {LeftPanel()}

        {/* Left restore tab */}
        {leftCollapsed && (
          <button onClick={() => setLeftCollapsed(false)} className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-16 w-5 rune-border rounded-r bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" title="Espandi colonna sinistra">
            <ChevronRight className="w-3 h-3 text-gold" />
          </button>
        )}
        {!leftCollapsed && (
          <button onClick={() => setLeftCollapsed(true)} className="absolute top-1/2 -translate-y-1/2 z-20 h-16 w-5 rune-border rounded-r bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" style={{ left: '288px' }} title="Comprimi colonna sinistra">
            <ChevronLeft className="w-3 h-3 text-gold" />
          </button>
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex overflow-hidden">
            {DrawingToolbar()}

            {/* MAP AREA */}
            <div
              ref={mapContainerRef}
              className="flex-1 relative overflow-hidden bg-black/70"
              onPointerMove={(e) => { onMapMove(e); handleMapPointerMoveViewport(e); }}
              onPointerDown={handleMapPointerDown}
              onPointerUp={(e) => { endDrag(e); handleMapPointerUpViewport(); }}
              onPointerLeave={(e) => { endDrag(e); handleMapPointerUpViewport(); }}
              onContextMenu={(e) => { if (isMiddlePanning.current) e.preventDefault(); }}
              style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
            >
              {/* Map title bar */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-card/90 backdrop-blur border border-yellow-900/40 rounded-full px-4 py-1.5 flex items-center gap-2">
                <MapIcon className="w-3 h-3 text-gold" />
                <span className="font-display text-sm text-parchment">{table.name}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </div>

              {/* Zoom in-map controls (top-right of map) */}
              <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
                <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="w-8 h-8 rune-border rounded bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center"><Plus className="w-3.5 h-3.5 text-gold" /></button>
                <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} className="w-8 h-8 rune-border rounded bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center"><Minus className="w-3.5 h-3.5 text-gold" /></button>
                <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="w-8 h-8 rune-border rounded bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center"><ExternalLink className="w-3.5 h-3.5 text-gold" /></button>
              </div>

              {/* Grid toggle bottom-right */}
              <div className="absolute bottom-2 right-2 z-10">
                <button onClick={toggleGrid} className="rune-border rounded bg-card/90 px-2 py-1 flex items-center gap-1 text-[10px] font-heading">
                  <Grid className={`w-3 h-3 ${table.map.showGrid ? 'text-gold' : 'text-muted-foreground'}`} /> GRID
                </button>
              </div>

              <div
                ref={mapRef}
                className="absolute inset-0 origin-top-left"
                style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, width: table.map.width, height: table.map.height }}
              >
                <img src={table.map.imageUrl} alt="map" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
                {table.map.showGrid && (
                  <div className="absolute inset-0 grid-overlay pointer-events-none" style={{ backgroundSize: `${gridSize}px ${gridSize}px`, width: table.map.width, height: table.map.height }} />
                )}
                {table.tokens.map(token => {
                  const char = characters.find(c => c.id === token.characterId);
                  const tokenImg = char?.tokenImage;
                  const size = gridSize * (token.size || 1);
                  const isActive = activeInitChar?.characterId === token.characterId;
                  return (
                    <div key={token.id} onPointerDown={(e) => startDrag(e, token)}
                      onClick={() => char && setSelectedChar(char)}
                      data-vtt-token="1"
                      className="vtt-token-el absolute cursor-grab active:cursor-grabbing token-shadow select-none"
                      style={{ left: token.x, top: token.y, width: size, height: size, transition: draggingToken?.id === token.id ? 'none' : 'left 0.15s, top 0.15s' }}>
                      <div className={`w-full h-full rounded-full border-[3px] flex items-center justify-center relative overflow-hidden ${isActive ? 'animate-pulse' : ''}`}
                        style={{
                          backgroundColor: tokenImg ? 'transparent' : token.color,
                          borderColor: isActive ? '#fbbf24' : (token.isNpc ? '#dc2626' : '#d4af37'),
                          boxShadow: isActive ? '0 0 24px #fbbf24, 0 0 12px rgba(0,0,0,0.9)' : '0 0 12px rgba(0,0,0,0.9)'
                        }}>
                        {tokenImg ? <img src={tokenImg} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} /> : (
                          <span className="font-heading font-bold text-white text-xs drop-shadow-md">{token.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-black/85 px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap font-heading text-parchment">{token.name}</div>
                    </div>
                  );
                })}
              </div>

              {lastRoll && (
                <div className="absolute bottom-16 right-4 z-10 rune-border bg-card/95 backdrop-blur rounded-lg px-4 py-2 arcane-glow max-w-xs animate-in fade-in slide-in-from-right">
                  <div className="text-[10px] font-heading text-gold">{lastRoll.userName}</div>
                  <div className="font-serif-fantasy italic text-xs">{lastRoll.label || lastRoll.formula}</div>
                  <div className="text-2xl font-display text-parchment">{lastRoll.total}</div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom panel restore tab */}
          {bottomCollapsed && (
            <button onClick={() => setBottomCollapsed(false)} className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-16 h-5 rune-border rounded-t bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" title="Espandi barra inferiore">
              <ChevronUp className="w-3 h-3 text-gold" />
            </button>
          )}
          {!bottomCollapsed && (
            <button onClick={() => setBottomCollapsed(true)} className="absolute left-1/2 -translate-x-1/2 z-20 w-16 h-5 rune-border rounded-t bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" style={{ bottom: '208px' }} title="Comprimi barra inferiore">
              <ChevronDown className="w-3 h-3 text-gold" />
            </button>
          )}
          {BottomPanel()}
        </div>

        {/* Right restore tab */}
        {rightCollapsed && (
          <button onClick={() => setRightCollapsed(false)} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-16 w-5 rune-border rounded-l bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" title="Espandi colonna destra">
            <ChevronLeft className="w-3 h-3 text-gold" />
          </button>
        )}
        {!rightCollapsed && (
          <button onClick={() => setRightCollapsed(true)} className="absolute top-1/2 -translate-y-1/2 z-20 h-16 w-5 rune-border rounded-l bg-card/90 hover:bg-yellow-900/30 flex items-center justify-center" style={{ right: '384px' }} title="Comprimi colonna destra">
            <ChevronRight className="w-3 h-3 text-gold" />
          </button>
        )}
        {RightPanel()}
      </div>

      <CharacterDialog open={charDialogOpen} onOpenChange={setCharDialogOpen} character={editingChar} tableId={tableId} onSaved={onCharSaved} onDeleted={onCharDeleted} />

      {/* Hidden YouTube audio player — always mounted so playback survives popover close */}
      <div
        id={YT_CONTAINER_ID + '-wrap'}
        aria-hidden="true"
        style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none', overflow: 'hidden' }}
      >
        <div id={YT_CONTAINER_ID} />
      </div>
    </div>
  );
}

// ============ ROOT APP ============
function VTTAccessGate({ user, onGranted }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ kind: 'form' });
  //   { kind: 'form' }
  //   { kind: 'error',   message: string }
  //   { kind: 'success', product: {product_id, name} }
  //   { kind: 'other',   product: {product_id, name} }

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const resp = await fetch(`${BACKEND}/api/redeem-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const detail = data?.detail;
        const message =
          typeof detail === 'string'
            ? detail
            : detail?.message || 'Codice non valido o non riscattabile.';
        setState({ kind: 'error', message });
      } else {
        const product = data?.product || {};
        if (product.product_id === 'mytharion_vtt_beta') {
          setState({ kind: 'success', product });
        } else {
          setState({ kind: 'other', product });
        }
      }
    } catch {
      setState({ kind: 'error', message: 'Errore di rete durante il riscatto.' });
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setCode('');
    setState({ kind: 'form' });
  };

  if (state.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-md rune-border bg-card/80 backdrop-blur border-ruby/40" data-testid="vtt-redeem-error">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-ruby flex items-center gap-2">
              <X className="w-6 h-6" /> Codice non valido
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {state.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={resetForm}
              data-testid="vtt-redeem-retry"
              className="w-full"
            >
              Riprova con un altro codice
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.kind === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-md rune-border bg-card/80 backdrop-blur border-gold/60 gold-glow" data-testid="vtt-redeem-success">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-gold flex items-center gap-2">
              <Sparkles className="w-6 h-6" /> VTT sbloccato!
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Accesso beta al Virtual Tabletop attivato per <span className="text-gold">{user?.email || 'il tuo account'}</span>. Preparati a forgiare la tua leggenda.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => onGranted?.()}
              data-testid="vtt-redeem-enter"
              className="w-full"
            >
              <ChevronRight className="w-4 h-4 mr-2" /> Entra nel VTT
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.kind === 'other') {
    const p = state.product || {};
    const isCarto = String(p.product_id || '').startsWith('cartomancer');
    const isPremium = p.product_id === 'premium_upgrade' || p.product_id === 'cartomancer_premium';
    const targetHref = isCarto || isPremium ? '/app' : '/dashboard';
    const targetLabel = isCarto || isPremium ? 'Apri Cartomancer' : 'Vai al Dashboard';
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-md rune-border bg-card/80 backdrop-blur border-arcane/50" data-testid="vtt-redeem-other">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-arcane flex items-center gap-2">
              <Info className="w-6 h-6" /> Codice riscattato — ma non è per il VTT
            </CardTitle>
            <CardDescription className="text-muted-foreground space-y-2">
              <p>
                Il codice era valido e ha sbloccato:{' '}
                <span className="text-gold font-heading">{p.name || p.product_id}</span>.
              </p>
              <p className="text-xs">
                Questa funzione <strong>non riguarda il Virtual Tabletop</strong>. Per accedere al VTT serve un codice specifico
                <span className="font-mono text-arcane"> MytharionVTT Beta</span>. Il tuo prodotto è stato comunque attivato correttamente.
              </p>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <a
              href={targetHref}
              className="inline-flex w-full items-center justify-center gap-2 h-10 px-4 rounded-md bg-arcane text-white hover:bg-arcane/90 font-heading text-sm"
              data-testid="vtt-redeem-other-open"
            >
              <ExternalLink className="w-4 h-4" /> {targetLabel}
            </a>
            <Button
              onClick={resetForm}
              variant="ghost"
              className="w-full"
              data-testid="vtt-redeem-other-retry"
            >
              Ho un altro codice per il VTT
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // form
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-md rune-border bg-card/80 backdrop-blur">
        <CardHeader>
          <CardTitle className="font-display text-3xl text-gold flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Accesso Beta VTT
          </CardTitle>
          <CardDescription>
            Ciao {user?.displayName || user?.email || 'avventuriero'}, per accedere al Virtual Tabletop serve un codice beta MytharionVTT.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="vtt-code">Codice Beta</Label>
              <Input
                id="vtt-code"
                data-testid="vtt-redeem-code-input"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="MYTH-XXXX-XXXX-XXXX"
                className="mt-2 font-mono"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="vtt-redeem-submit"
              className="w-full"
            >
              {busy ? 'Verifica…' : 'Sblocca VTT'}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Non hai un codice? Contatta l'amministratore o richiedilo dal supporto.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function VTT() {
  const navigate = useNavigate();
  const [view, setView] = useState('checking');
  const [user, setUser] = useState(null);
  const [openTableId, setOpenTableId] = useState(null);

  const runAccessCheck = async () => {
    try {
      const resp = await fetch(`${BACKEND}/api/mytharion-vtt/access`, { credentials: 'include' });
      if (resp.status === 401) {
        setView('unauthenticated');
        return;
      }
      const data = await resp.json();
      setUser(data.user);
      if (data.has_vtt_access) {
        setView('dashboard');
      } else {
        setView('gate');
      }
    } catch (e) {
      setView('unauthenticated');
    }
  };

  useEffect(() => {
    runAccessCheck();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    navigate('/');
  };

  if (view === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-rune-spin w-16 h-16 rounded-full rune-border gold-glow flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-gold" />
        </div>
      </div>
    );
  }

  if (view === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-md rune-border bg-card/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-gold">Accesso richiesto</CardTitle>
            <CardDescription>Effettua il login con Google per accedere al Virtual Tabletop.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={() => navigate('/login')}
              data-testid="vtt-login-btn"
              className="w-full"
            >
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (view === 'gate') {
    return <VTTAccessGate user={user} onGranted={runAccessCheck} />;
  }

  if (view === 'dashboard') {
    return (
      <Dashboard
        user={{
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          avatar: user.avatar,
        }}
        onLogout={handleLogout}
        onOpenTable={(id) => { setOpenTableId(id); setView('game'); }}
      />
    );
  }

  if (view === 'game') {
    return (
      <GameView
        tableId={openTableId}
        user={{
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          avatar: user.avatar,
        }}
        onExit={() => { setOpenTableId(null); setView('dashboard'); }}
      />
    );
  }

  return null;
}

export default VTT;
